
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { toast } from '@/components/ui/use-toast';

const DeleteAllChatsButton = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { chats, deleteChat } = useSupabaseStore();
  
  const handleDeleteAllChats = async () => {
    if (isDeleting || !chats.length) return;
    
    setIsDeleting(true);
    
    try {
      // Process chats in parallel for better performance
      const deletePromises = chats.map(chat => deleteChat(chat.id));
      await Promise.all(deletePromises);
      
      toast({
        title: "Success",
        description: `Deleted ${chats.length} chat${chats.length === 1 ? '' : 's'}.`,
      });
    } catch (error) {
      console.error('Error deleting all chats:', error);
      toast({
        title: "Error",
        description: "Failed to delete all chats. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant="ghost-destructive" 
          size="sm"
          className="gap-1 text-destructive hover:text-destructive-foreground transition-colors"
          disabled={isDeleting || chats.length === 0}
        >
          <Trash2 size={16} />
          Delete All
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete All Chats</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete all {chats.length} chat{chats.length === 1 ? '' : 's'} and their messages. 
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteAllChats}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete All'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAllChatsButton;
